<template>
  <div>
    <div id="visualisationCanvasContainer" ref="container" class="visualisationCanvasContainer" />
    <Spinner v-if="loading" />
    <div v-else>
      <template v-if="!isMobile">
        <div
          v-for="position in widgetPositions"
          :key="position"
          :class="`widgets-container position-widgets-${position}`"
        >
          <FilterBubblesUI v-if="filterByCategoryPosition === position" />
          <slot name="widgets" :widgets-container-position="position" />
        </div>
      </template>
      <TouchableIndicator v-if="showTouchableIndicator" />
      <VisualisationControls
        v-if="!isMobile"
        @zoom-in="zoomIn"
        @zoom-out="zoomOut"
        @recenter="recenter"
      />
      <Teleport v-if="isMobile" to="body">
        <MobileVisualisationPopup :popup-details="popupDetails" />
      </Teleport>
      <Teleport v-else to="#main-panel">
        <VisualisationPopup v-element-hover="onPopupHover" :popup-details="popupDetails" />
      </Teleport>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import Konva from 'konva'
import { CURSOR_STYLES, TAB_OFFSET, UI_EVENTS, defaultCoordinate } from '@/constants'
import { vElementHover } from '@vueuse/components'
import AbstractComponent from '@/components/common/AbstractComponent'
import Spinner from '@/components/public/Spinner.vue'
import FilterBubblesUI from '@/components/public/FilterByCategory/FilterBubblesUI.vue'
import VisualisationPopup from '@/components/public/Popup/VisualisationPopup.vue'
import MobileVisualisationPopup from '@/components/public/Popup/MobileVisualisationPopup.vue'
import VisualisationControls from '@/components/public/Visualisations/VisualisationControls.vue'
import {
  Coordinate2D,
  Dimensions,
  LegendPosition,
  LegendPositions,
  Path,
  Rectangle,
  VisualisationPopupDetails,
} from '@/types'
import interact from 'interactjs'
import { coordinatesDelta, rectCenter } from '@/helpers'
import { Shapes } from '@/components/public/Visualisations/types'
import logger from '@/plugins/logging/logger'
import Teleport from 'vue2-teleport' // TODO: Remove this import and component when migrating to vue3
import TouchableIndicator from '@/components/public/mobile/TouchableIndicator.vue'
import { settingsService } from '@/services'
import { BoundingBoxRect } from '@/components/public/Visualisations/Debugging'

const POPUP_HOVER_DELAY = 50
const DEFAULT_USER_ZOOM_SCALE = 1
const POINTER_MOVE_TOLERANCE = 2 // the distance the pointer/finger(s) must be moved before an action sequence occurs
const GESTURE_ZOOM_SENSITIVITY_FACTOR = 0.15 // this slows down or speeds up gesture zooming
const MOUSE_WHEEL_SCALE_FACTOR = 1.1 // this controls the amount of zoom per mouse wheel event
const ZOOM_TO_ENTITY_MARGIN = 15 // the margin around the entity when it is zoomed to fit the container
const FOCUS_ANIMATION_DURATION = 500 // how long it takes to pan and zoom to an entity when focusing on it
const FADE_DURATION_SECONDS = 0.2 // how long it takes to fade in/out an entity
const TOUCHABLE_INDICATOR_DISMISSED_KEY = 'touchableIndicatorDismissed'
const TOUCHABLE_INDICATOR_DISMISSED_VALUE = 'true'

@AbstractComponent({
  directives: {
    elementHover: vElementHover,
  },
  components: {
    FilterBubblesUI,
    MobileVisualisationPopup,
    Spinner,
    Teleport,
    VisualisationPopup,
    VisualisationControls,
    TouchableIndicator,
  },
  watch: {
    position(newPosition: Coordinate2D) {
      // @ts-ignore
      if (this.stage && newPosition) {
        // @ts-ignore
        this.stage?.position(newPosition)
      }
    },
    scale(newScale: number) {
      // @ts-ignore
      this.stage?.scale({ x: newScale, y: newScale })
    },
    storyPath: {
      handler(newPath: Path, oldPath: Path) {
        // @ts-ignore
        this.storyPathUpdated(newPath, oldPath)
      },
      immediate: true,
    },
    loaded: {
      handler(loaded: boolean) {
        if (loaded) {
          // @ts-ignore
          this.onLoaded()
        }
      },
      immediate: true,
    },
    baseOffset: {
      handler(offset: Coordinate2D) {
        // @ts-ignore
        this.layer.offset(offset)
      },
      immediate: true,
    },
    initialViewBoundingRect() {
      // @ts-ignore
      this.updateInitialViewBoundingBox()
    },
    currentViewBoundingRect() {
      // @ts-ignore
      this.updateCurrentViewBoundingBox()
    },
  },
})
export default abstract class VisualisationCanvas extends Vue {
  loading: boolean = true
  private stage: Konva.Stage | undefined
  private layer: Konva.Layer = new Konva.Layer()
  private entities: Shapes.Entity[] = []
  protected containerSize: Dimensions = { width: 0, height: 0 }
  protected userZoomScale: number = DEFAULT_USER_ZOOM_SCALE
  protected userPanOffset: Coordinate2D = defaultCoordinate()
  private resizeObserver: ResizeObserver | null = null
  private holding: boolean = false
  private touchHighlightedEntity: Shapes.HighlightableEntity | null = null
  private popupEntity: Shapes.HasPopupEntity | null = null
  private touchableIndicatorDismissed: boolean = false
  protected baseOffset: Coordinate2D = defaultCoordinate()
  protected initialViewBoundingBox: BoundingBoxRect = new BoundingBoxRect('red')
  protected currentViewBoundingBox: BoundingBoxRect = new BoundingBoxRect('black')
  private hoverTimeout: number | null = null

  mounted() {
    this.initialise()
  }

  beforeDestroy() {
    this.resizeObserver && this.resizeObserver.disconnect()
  }

  private get container() {
    return this.$refs.container as HTMLDivElement
  }

  get minimumCanvasSideLength() {
    return this.$typedStore.public.display.isIOS ? 1000 : 2000
  }

  get initialViewBoundingRect() {
    const scaledCanvasSize = this.minimumCanvasSideLength / this.canvasToContainerScale
    const offsetX = (this.containerSize.width - scaledCanvasSize) / 2
    const offsetY = (this.containerSize.height - scaledCanvasSize) / 2

    const left = -offsetX * this.canvasToContainerScale
    const top = -offsetY * this.canvasToContainerScale

    return new Rectangle({
      top,
      left,
      bottom: top + this.containerSizeOnCanvas.height,
      right: left + this.containerSizeOnCanvas.width,
    })
  }

  get currentViewBoundingRect() {
    const top = -this.position.y / this.scale
    const left = (-this.position.x + TAB_OFFSET) / this.scale
    const bottom = top + this.containerSizeOnCanvas.height / this.userZoomScale
    const right = left + this.containerSizeOnCanvas.width / this.userZoomScale

    return new Rectangle({
      top,
      left,
      bottom,
      right,
    })
  }

  get canvasCenter() {
    return {
      x: this.minimumCanvasSideLength / 2,
      y: this.minimumCanvasSideLength / 2,
    }
  }

  get widgetPositions(): LegendPosition[] {
    return [LegendPositions.TOP_LEFT, LegendPositions.BOTTOM_LEFT, LegendPositions.BOTTOM_RIGHT]
  }

  get storyPath() {
    return this.$typedStore.visualisationData.storyPath
  }

  get scale() {
    return this.containerToCanvasScale * this.additionalScale * this.userZoomScale
  }

  get position(): Coordinate2D {
    const scaledCanvasSize = this.minimumCanvasSideLength * this.scale
    let x = (this.containerSize.width - scaledCanvasSize) / 2 + TAB_OFFSET
    let y = (this.containerSize.height - scaledCanvasSize) / 2
    x += this.userPanOffset.x
    y += this.userPanOffset.y
    return { x, y }
  }

  get filterByCategoryPosition() {
    return this.$typedStore.activeVisualisation.visualisationStore.app.config.filterByCategory
      .position
  }

  get isMobile() {
    return this.$typedStore.public.display.isMobile
  }

  get loaded() {
    return !this.loading
  }

  get popupDetails(): VisualisationPopupDetails | null {
    if (this.popupEntity) {
      const { popupDetails, center, left, top } = this.popupEntity
      return {
        ...popupDetails,
        entityCenter: this.canvasToContainerPoint(center),
        entityTopLeft: this.canvasToContainerPoint({ x: left, y: top }),
      }
    }

    return null
  }

  get showTouchableIndicator() {
    return this.isMobile && !this.touchableIndicatorDismissed
  }

  protected get containerSizeOnCanvas() {
    if (this.containerToCanvasScale === 0) return { width: 0, height: 0 }

    return {
      width: this.containerSize.width / this.containerToCanvasScale,
      height: this.containerSize.height / this.containerToCanvasScale,
    }
  }

  protected get containerClientCenter() {
    const clientRect = this.container.getBoundingClientRect()
    return rectCenter(clientRect)
  }

  protected get defaultUserZoomScale() {
    return DEFAULT_USER_ZOOM_SCALE
  }

  protected get defaultPosition() {
    return defaultCoordinate()
  }

  protected get additionalScale(): number {
    return 1
  }

  protected get canvasToContainerScale() {
    const shortestSide = Math.min(this.containerSize.width, this.containerSize.height)
    return this.minimumCanvasSideLength / shortestSide
  }

  protected get containerToCanvasScale() {
    const shortestSide = Math.min(this.containerSize.width, this.containerSize.height)
    return shortestSide / this.minimumCanvasSideLength
  }

  private get selectableEntities(): Shapes.SelectableEntity[] {
    return this.entities.filter((entity) =>
      Shapes.isSelectable(entity),
    ) as Shapes.SelectableEntity[]
  }

  clear() {
    this.layer.removeChildren()
  }

  recenter() {
    this.startFocusAnimation(this.defaultUserZoomScale, this.defaultPosition)
  }

  reset() {
    this.clear()
    this.startFocusAnimation(this.defaultUserZoomScale, this.defaultPosition)
  }

  zoomIn() {
    this.applyZoom(1.1, this.containerClientCenter)
  }

  zoomOut() {
    this.applyZoom(0.9, this.containerClientCenter)
  }

  onLoaded() {
    this.stage?.visible(true)
    this.selectedableEntitiesByPath(this.storyPath).forEach((entity) => {
      entity.isSelected = true
      if (Shapes.isFocusable(entity)) {
        this.focusOnEntity(entity)
      }
    })
  }

  focusOnSelectedEntities() {
    this.selectableEntities.forEach((entity) => {
      if (entity.isSelected && Shapes.isFocusable(entity)) {
        this.focusOnEntity(entity)
      }
    })
  }

  addShape(shape: Shapes.Shape) {
    this.initialiseEntity(shape)
    this.addEntity(shape)
  }

  addGroup(group: Shapes.Group) {
    this.initialiseEntity(group)
    group.entities.forEach(this.initialiseEntity)

    this.addEntity(group)
  }

  removeShape(shape: Shapes.Shape) {
    this.removeEntity(shape)
  }

  removeGroup(group: Shapes.Group) {
    this.removeEntity(group)
  }

  private initialiseEntity(entity: Shapes.Entity) {
    this.setEntityName(entity)
    this.attachHighlightable(entity)
    this.attachSelectable(entity)
    this.attachPopup(entity)
    this.attachDraggable(entity)
    this.enablePerfectDraw(entity)
    this.entities.push(entity)
  }

  private setEntityName(entity: Shapes.Entity) {
    if (entity.name()) {
      logger.warn(
        `${entity.nodeType} added to VisualisationCanvas with a name, the name will be overridden by the entity path`,
      )
    }
    entity.name(entity.path.toString())
  }

  private attachHighlightable(entity: Shapes.Entity) {
    if (Shapes.isHighlightable(entity)) {
      entity.on(UI_EVENTS.MouseOver, () => {
        entity.isHighlighted = true
      })
      entity.on(UI_EVENTS.MouseOut, () => {
        entity.isHighlighted = false
      })
    }
  }

  private attachSelectable(entity: Shapes.Entity) {
    if (Shapes.isSelectable(entity)) {
      entity.on(UI_EVENTS.MouseOver, () => {
        this.container.style.cursor = CURSOR_STYLES.Pointer
      })
      entity.on(UI_EVENTS.MouseOut, () => {
        this.container.style.cursor = CURSOR_STYLES.Default
      })
    }
  }

  private attachPopup(entity: Shapes.Entity) {
    if (Shapes.hasPopup(entity)) {
      entity.on(UI_EVENTS.MouseMove, () => {
        if (this.hoverTimeout) clearTimeout(this.hoverTimeout)
        this.hoverTimeout = window.setTimeout(() => {
          this.popupEntity = entity
        }, POPUP_HOVER_DELAY)
      })
      entity.on(UI_EVENTS.MouseOut, () => {
        this.hidePopupWithDelay()
      })
      entity.on(UI_EVENTS.Click, () => {
        if (this.hoverTimeout) clearTimeout(this.hoverTimeout)
        this.popupEntity = null
      })
    }
  }

  private attachDraggable(entity: Shapes.Entity) {
    if (Shapes.isDraggable(entity)) {
      entity.draggable(true)

      // To allow the Konva drag events to take place
      // without dragging the whole canvas we need to
      // enable/disable the interactjs event listeners
      // on the canvas container when the user moves
      // their mouse in/out of the draggable entities
      entity.on(UI_EVENTS.MouseOver, () => {
        interact(this.container).draggable(false)
        this.container.style.cursor = CURSOR_STYLES.Move
      })

      entity.on(UI_EVENTS.MouseOut, () => {
        interact(this.container).draggable(true)
        this.container.style.cursor = CURSOR_STYLES.Default
      })

      if (entity.dragstart) {
        entity.on(UI_EVENTS.DragStart, entity.dragstart)
      }

      if (entity.dragmove) {
        entity.on(UI_EVENTS.DragMove, entity.dragmove)
      }

      if (entity.dragend) {
        entity.on(UI_EVENTS.DragEnd, entity.dragend)
      }
    }
  }

  private enablePerfectDraw(entity: Shapes.Entity) {
    if (Shapes.isShape(entity)) {
      entity.perfectDrawEnabled(false)
    }
  }

  private onPopupHover(state: boolean) {
    if (state) {
      this.hoverTimeout && clearTimeout(this.hoverTimeout)
    } else {
      this.hidePopupWithDelay()
    }
  }

  private hidePopupWithDelay() {
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout)
      if (this.popupDetails) {
        this.hoverTimeout = window.setTimeout(() => {
          this.popupEntity = null
        }, POPUP_HOVER_DELAY)
      }
    }
  }

  private addEntity(entity: Shapes.Entity) {
    if (Shapes.isFadable(entity)) {
      this.fadeInEntity(entity)
    } else {
      this.layer.add(entity)
      entity.onAdded?.()
    }
  }

  private removeEntity(entity: Shapes.Entity) {
    if (Shapes.isFadable(entity)) {
      this.fadeOutEntity(entity)
    } else {
      this.destroyEntity(entity)
    }
  }

  private destroyEntity(entity: Shapes.Entity) {
    this.entities = this.entities.filter((existing) => existing !== entity)
    entity.destroy()
  }

  private initialise() {
    this.initialiseResizeHandler()
    this.initialiseCanvas()
    this.initialiseTouchableIndicator()
    this.initialiseInteractions()
    this.initialiseDebugMode()
  }

  private initialiseCanvas() {
    this.stage = new Konva.Stage({
      container: this.container,
      width: this.minimumCanvasSideLength,
      height: this.minimumCanvasSideLength,
      visible: false,
    })

    this.stage.add(this.layer)
    this.layer.offset(this.baseOffset)
    this.stage.on(UI_EVENTS.MouseMove, (evt) => {
      if (evt.target instanceof Konva.Stage) {
        this.hidePopupWithDelay()
      }
    })

    this.updateCurrentViewBoundingBox()
    this.currentViewBoundingBox.visible(false)
    this.addShape(this.currentViewBoundingBox)
  }

  private initialiseDebugMode() {
    if (settingsService.bubblesCanvasDebugMode) {
      this.updateInitialViewBoundingBox()
      this.addShape(this.initialViewBoundingBox)
      this.currentViewBoundingBox.visible(true)
    }
  }

  private initialiseResizeHandler() {
    this.resizeObserver = new ResizeObserver(this.handleResize)
    this.resizeObserver.observe(this.container)

    this.handleResize()
  }

  protected handleResize() {
    this.containerSize = {
      width: this.container.clientWidth - TAB_OFFSET,
      height: this.container.clientHeight,
    }
  }

  private updateInitialViewBoundingBox() {
    this.initialViewBoundingBox.update(this.initialViewBoundingRect)
  }

  private updateCurrentViewBoundingBox() {
    this.currentViewBoundingBox.update(this.currentViewBoundingRect)
  }

  private initialiseInteractions() {
    interact.pointerMoveTolerance(POINTER_MOVE_TOLERANCE)

    interact(this.container, {
      styleCursor: false,
    })
      .on(UI_EVENTS.TouchStart, this.dismissTouchableIndicator)
      .on(UI_EVENTS.Tap, this.handleTap)
      .on(UI_EVENTS.Hold, this.handleHold)
      .on(UI_EVENTS.TouchEnd, this.handleHoldEnd)
      .draggable({
        listeners: {
          move: this.handleDrag,
        },
      })
      .gesturable({
        listeners: {
          move: this.handleGestureZoom,
        },
      })

    // interactjs does not handle mouse wheel events
    this.container.addEventListener('wheel', this.handleMouseWheelZoom)
  }

  private initialiseTouchableIndicator() {
    try {
      this.touchableIndicatorDismissed =
        sessionStorage.getItem(TOUCHABLE_INDICATOR_DISMISSED_KEY) ===
        TOUCHABLE_INDICATOR_DISMISSED_VALUE
    } catch (error: any) {
      logger.error(error.toString())
    }
  }

  private dismissTouchableIndicator() {
    if (this.isMobile) {
      this.touchableIndicatorDismissed = true
      try {
        sessionStorage.setItem(
          TOUCHABLE_INDICATOR_DISMISSED_KEY,
          TOUCHABLE_INDICATOR_DISMISSED_VALUE,
        )
      } catch (error: any) {
        logger.error(error.toString())
      }
    }
  }

  protected handleTap(event: Interact.PointerEvent) {
    // tap gets triggered at the end of a hold, we want to ignore these events
    if (!this.holding) {
      const entity = this.selectableEntityFromPoint({ x: event.offsetX, y: event.offsetY })
      if (entity) {
        this.$typedStore.activeVisualisation.updateStoryPath(entity.path)
      }
    }
  }

  protected handleHold(event: Interact.PointerEvent) {
    if (this.isTouch(event)) {
      this.holding = true

      const entity = this.entityFromPoint({
        x: event.offsetX,
        y: event.offsetY,
      })

      this.touchHighlightedEntity = Shapes.isHighlightable(entity) ? entity : null
      if (this.touchHighlightedEntity) {
        this.touchHighlightedEntity.isHighlighted = true
      }

      if (Shapes.hasPopup(entity)) {
        this.popupEntity = entity
      }
    }
  }

  protected handleHoldEnd() {
    this.holding = false

    if (this.touchHighlightedEntity) {
      this.touchHighlightedEntity.isHighlighted = false
      this.touchHighlightedEntity = null
    }

    this.popupEntity = null
  }

  protected handleDrag(event: Interact.DragEvent) {
    this.userPanOffset.x += event.dx
    this.userPanOffset.y += event.dy
  }

  protected handleGestureZoom(event: Interact.GestureEvent) {
    // apply the zoom sensitity factor to the gesture scale
    const zoomMultiplier = 1 + (event.scale - 1) * GESTURE_ZOOM_SENSITIVITY_FACTOR
    this.applyZoom(zoomMultiplier, event.client)
  }

  protected handleMouseWheelZoom(event: WheelEvent) {
    event.preventDefault()
    const zoomMultiplier =
      event.deltaY > 0 ? 1 / MOUSE_WHEEL_SCALE_FACTOR : MOUSE_WHEEL_SCALE_FACTOR
    this.applyZoom(zoomMultiplier, { x: event.clientX, y: event.clientY })
  }

  protected storyPathUpdated(newPath: Path, oldPath: Path) {
    if (oldPath) {
      this.dismissTouchableIndicator()
    }

    this.selectedableEntitiesByPath(oldPath).forEach((entity) => {
      entity.isSelected = false
    })

    if (newPath.isRootOrSubroot) {
      this.recenter()
    } else {
      this.selectedableEntitiesByPath(newPath).forEach((entity) => {
        entity.isSelected = true
        if (Shapes.isFocusable(entity)) {
          this.focusOnEntity(entity)
        }
      })
    }
  }

  private isTouch(event: Interact.PointerEvent) {
    return event.pointerType === 'touch'
  }

  private applyZoom(zoomMultiplier: number, clientCenterPoint: Coordinate2D) {
    const originalPinchCenter = this.containerToCanvasPoint(clientCenterPoint)
    this.userZoomScale *= zoomMultiplier
    const newPinchCenter = this.containerToCanvasPoint(clientCenterPoint)

    const centerDelta = coordinatesDelta(newPinchCenter, originalPinchCenter)

    this.userPanOffset.x += centerDelta.x * this.scale
    this.userPanOffset.y += centerDelta.y * this.scale
  }

  private containerToCanvasPoint(clientPoint: Coordinate2D): Coordinate2D {
    const clientRect = this.container.getBoundingClientRect()
    const canvasPoint = {
      x: clientPoint.x - clientRect.left,
      y: clientPoint.y - clientRect.top,
    }

    // Adjust for pan and scale
    canvasPoint.x = (canvasPoint.x - this.position.x) / this.scale
    canvasPoint.y = (canvasPoint.y - this.position.y) / this.scale

    return canvasPoint
  }

  private canvasToContainerPoint(canvasPoint: Coordinate2D): Coordinate2D {
    // Adjust for scale, pan and base offset
    const x = canvasPoint.x * this.scale + this.position.x - this.baseOffset.x * this.scale
    const y = canvasPoint.y * this.scale + this.position.y - this.baseOffset.y * this.scale

    return { x, y }
  }

  private selectedableEntitiesByPath(path: Path) {
    return this.selectableEntities.filter((entity) => {
      return entity.path.equals(path)
    }) as Shapes.SelectableEntity[]
  }

  private entityFromPoint(point: Coordinate2D): Shapes.Entity | null {
    const entity = this.stage?.getIntersection(point)
    return Shapes.isEntity(entity) ? entity : null
  }

  private selectableEntityFromPoint(point: Coordinate2D): Shapes.SelectableEntity | null {
    const entity = this.entityFromPoint(point)
    return Shapes.isSelectable(entity) ? entity : null
  }

  private fadeInEntity(entity: Shapes.Entity) {
    entity.opacity(0)
    this.layer.add(entity)
    entity.onAdded?.()
    entity.to({
      opacity: 1,
      easing: Konva.Easings.EaseInOut,
      duration: FADE_DURATION_SECONDS,
    })
  }

  private fadeOutEntity(entity: Shapes.Entity) {
    if (!Shapes.isFadable(entity)) return

    entity.to({
      opacity: 0,
      radius: 0,
      easing: Konva.Easings.EaseInOut,
      duration: FADE_DURATION_SECONDS,
      onFinish: () => {
        this.destroyEntity(entity)
      },
    })
  }

  private focusOnEntity(entity: Shapes.Entity) {
    if (!Shapes.isFocusable(entity)) return

    const targetUserZoomScale = this.calculateUserZoomScaleForEntity(entity)
    const targetUserPanOffset = this.calculateUserPanOffsetToEntityCenter(
      entity,
      targetUserZoomScale,
    )

    this.startFocusAnimation(targetUserZoomScale, targetUserPanOffset)
  }

  private calculateUserZoomScaleForEntity(entity: Shapes.Entity) {
    const shortestSide = Math.min(entity.width(), entity.height())
    const scale = this.minimumCanvasSideLength / (shortestSide + ZOOM_TO_ENTITY_MARGIN)
    return scale / this.additionalScale
  }

  private calculateUserPanOffsetToEntityCenter(entity: Shapes.Entity, targetUserZoomScale: number) {
    const canvasCenter = this.minimumCanvasSideLength / 2
    const scale = this.containerToCanvasScale * this.additionalScale * targetUserZoomScale
    return {
      x: (canvasCenter - entity.center.x) * scale,
      y: (canvasCenter - entity.center.y) * scale,
    }
  }

  public startFocusAnimation(targetZoom: number, targetPan: Coordinate2D) {
    const initialZoom = this.userZoomScale
    const initialPan = { ...this.userPanOffset }
    const startTime = performance.now()

    const animateTransition = (currentTime: number) => {
      const elapsedTime = currentTime - startTime
      const progress = Math.min(elapsedTime / FOCUS_ANIMATION_DURATION, 1)

      this.userZoomScale = initialZoom + (targetZoom - initialZoom) * progress
      this.userPanOffset.x = initialPan.x + (targetPan.x - initialPan.x) * progress
      this.userPanOffset.y = initialPan.y + (targetPan.y - initialPan.y) * progress

      if (elapsedTime < FOCUS_ANIMATION_DURATION) {
        requestAnimationFrame(animateTransition) // Continue animation
      } else {
        // Ensure final values are set at the end of the animation
        this.userZoomScale = targetZoom
        this.userPanOffset.x = targetPan.x
        this.userPanOffset.y = targetPan.y
      }
    }

    requestAnimationFrame(animateTransition) // Start animation
  }
}
</script>

<style scoped>
.visualisationCanvasContainer {
  position: absolute;
  /* Offset the size of the active tab */
  margin-left: calc(var(--tab-offset) * -1);
  width: calc(100% + var(--tab-offset));
  min-width: 100%;
  height: 100%;
  min-height: 100%;
  padding: 0;
  touch-action: none;
  user-select: none;
  overflow: hidden;
}

.widgets-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 1;
}

.position-widgets-top-left {
  position: absolute;
  top: 0;
  left: 0;
  margin-top: 0.5rem;
  margin-left: 18px;
}

.position-widgets-bottom-left {
  position: absolute;
  bottom: 0;
  left: 0;
  margin-bottom: 0.5rem;
  margin-left: 18px;
}

.position-widgets-bottom-right {
  position: absolute;
  bottom: 0;
  right: 0;
  margin-bottom: 0.5rem;
  margin-right: 0.5rem;
}
</style>
